"use client";

import { useMeasure } from "@uidotdev/usehooks";
import {
  Disc3,
  ListMusic,
  Loader2,
  Pause,
  Pencil,
  Play,
  Send,
} from "lucide-react";
import { animate, motion, useMotionValue } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { MusicServiceChips } from "@/components/music/music-service-chips";
import { SongSheet } from "@/components/music/song-sheet";
import { useMusicPreview } from "@/components/music/use-music-preview";
import { Button } from "@/components/ui/button";
import { requestDeviceTiltAccess } from "@/components/wallet/use-device-tilt";
import { musicAttachmentFromProfileSong } from "@/lib/music/attachment";
import { musicArtworkSrc } from "@/lib/music/links";
import {
  PROFILE_SONG_FEATURED,
  type ProfileMusicSong,
} from "@/lib/music/types";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { CdCase } from "./cd-case";
import { MusicListDrawer } from "./music-list-drawer";
import { MusicPickerDrawer } from "./music-picker-drawer";
import {
  CASE_FRACTION,
  CASE_MAX_PX,
  MUSIC_STAGE_HEIGHT_CSS,
  OPEN_FIT,
} from "./music-stage";

/** Horizontal travel under which a drag still counts as a tap. */
const TAP_SLOP_PX = 10;

export interface ProfileMusicSectionProps {
  songs: ProfileMusicSong[];
  ownerUserId: string;
  ownerName: string | null;
  isOwnProfile: boolean;
  className?: string;
}

/**
 * "Music picks": the songs on a profile, each in its own CD case. Sits under
 * About me — an extra layer of personality, never the first thing you see.
 *
 * Visitors tap a case to open it and hear the preview; while it is open,
 * swiping moves to the next case already open and playing, so a taste can be
 * flipped through in seconds. Nothing autoplays on arrival.
 *
 * No songs, no section — for visitors AND owners. The owner's way in is the
 * small "Add music" button beside About me (`AddMusicLiquidButton`).
 */
export function ProfileMusicSection({
  songs,
  ownerUserId,
  ownerName,
  isOwnProfile,
  className,
}: ProfileMusicSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<"browse" | "list">("browse");
  const [listOpen, setListOpen] = useState(false);

  if (songs.length === 0) return null;

  // One ordered list: the first five are the cases; the rest live behind +N.
  const featured = songs.slice(0, PROFILE_SONG_FEATURED);
  const extra = songs.length - featured.length;
  const ownerFirstName = firstName(ownerName, isOwnProfile);
  const openPicker = (mode: "browse" | "list") => {
    setPickerMode(mode);
    setPickerOpen(true);
  };

  return (
    <section
      aria-label="Music picks"
      className={cn("flex flex-col space-y-3", className)}
    >
      <div className="flex h-7 items-center justify-between">
        <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
          Music picks
        </h3>
        <div className="flex items-center gap-1">
          {extra > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-text-secondary dark:hover:bg-white/10"
              onClick={() => setListOpen(true)}
            >
              <ListMusic className="size-3.5" />
              All {songs.length}
            </Button>
          ) : null}
          {isOwnProfile ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 px-2 text-xs text-text-secondary dark:hover:bg-white/10"
              onClick={() => openPicker("browse")}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
          ) : null}
        </div>
      </div>
      <MusicCarousel
        songs={featured}
        extraCount={extra}
        onOpenList={() => setListOpen(true)}
        ownerUserId={ownerUserId}
        ownerFirstName={ownerFirstName}
      />
      <MusicListDrawer
        open={listOpen}
        onOpenChange={setListOpen}
        songs={songs}
        ownerFirstName={ownerFirstName}
        ownerUserId={ownerUserId}
        onEdit={
          isOwnProfile
            ? () => {
                setListOpen(false);
                setTimeout(() => openPicker("list"), 220);
              }
            : undefined
        }
      />
      {isOwnProfile ? (
        <MusicPickerDrawer
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          initialSongs={songs}
          initialMode={pickerMode}
        />
      ) : null}
    </section>
  );
}

function firstName(name: string | null, own: boolean) {
  const first = name?.trim().split(/\s+/)[0];
  return first || (own ? "My" : "Their");
}

function MusicCarousel({
  songs,
  extraCount,
  onOpenList,
  ownerUserId,
  ownerFirstName,
}: {
  /** The songs on display (the first five of the list). */
  songs: ProfileMusicSong[];
  /** How many more sit in the list behind "+N". */
  extraCount: number;
  onOpenList: () => void;
  ownerUserId: string;
  ownerFirstName: string;
}) {
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const W = width ?? 0;
  const S = Math.round(Math.min(W * CASE_FRACTION, CASE_MAX_PX));
  // The open pair is 2S wide, and perspective plus the open tilt make its
  // near edges render ~8% wider than that — fit the pair to 88% of the column.
  const openScale = S > 0 ? Math.min(1, (W * OPEN_FIT) / (2 * S)) : 1;

  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [flourish, setFlourish] = useState(false);
  const x = useMotionValue(0);
  const draggedRef = useRef(false);

  const slides = songs.map((song, position) => ({ ...song, position }));
  const active = slides[Math.min(index, slides.length - 1)];
  const tracklist = slides.map((s) => ({
    id: s.id,
    title: s.title,
    artistName: s.artistName,
  }));

  // Songs can change under us (the owner just edited them).
  useEffect(() => {
    if (index > songs.length - 1) setIndex(Math.max(0, songs.length - 1));
  }, [index, songs.length]);

  useEffect(() => {
    x.set(-index * W);
  }, [W, index, x]);

  const goToRef = useRef<(next: number, autoplay: boolean) => void>(() => {});
  const preview = useMusicPreview({
    onEnded: (key) => {
      const i = slides.findIndex((s) => s.id === key);
      if (i >= 0 && i < slides.length - 1) goToRef.current(i + 1, true);
    },
  });
  const playing = preview.status === "playing" || preview.status === "loading";
  const target = (song: ProfileMusicSong) => ({
    key: song.id,
    previewUrl: song.previewUrl,
    ownerUserId,
    surface: "profile" as const,
  });

  const goTo = (next: number, autoplay: boolean) => {
    const clamped = Math.max(0, Math.min(slides.length - 1, next));
    animate(x, -clamped * W, { type: "spring", stiffness: 260, damping: 32 });
    if (clamped === index) return;
    setFlourish(false);
    setIndex(clamped);
    uiHaptics.select();
    const song = slides[clamped];
    if (open && song && (playing || autoplay)) {
      if (song.previewUrl) preview.play(target(song));
      else preview.pause();
    }
  };
  goToRef.current = goTo;

  const openCase = () => {
    if (!active) return;
    // iOS only grants motion from a tap; this is the tap that means "I'm
    // handling the case", as with the wallet stickers.
    requestDeviceTiltAccess();
    uiHaptics.tap();
    setFlourish(true);
    setOpen(true);
    // Inside the tap: the gesture iOS needs before it will make sound.
    if (active.previewUrl) preview.play(target(active));
  };
  const closeCase = () => {
    uiHaptics.tap();
    setOpen(false);
    preview.pause();
  };
  const togglePlay = () => {
    if (!active?.previewUrl) return;
    if (!open) {
      openCase();
      return;
    }
    preview.toggle(target(active));
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={measureRef}
        className="w-full"
        style={{ containerType: "inline-size" }}
      >
        {/* Fixed height, pure CSS — never waits for the measure, never
            changes on open/close. The case moves inside; the page never does. */}
        <div
          className="relative w-full overflow-hidden"
          style={{ height: MUSIC_STAGE_HEIGHT_CSS }}
        >
          {S > 0 ? (
            <motion.div
              className="absolute inset-y-0 left-0 flex touch-pan-y"
              style={{ x, width: W * slides.length }}
              drag={slides.length > 1 ? "x" : false}
              dragDirectionLock
              dragConstraints={{ left: -(slides.length - 1) * W, right: 0 }}
              dragElastic={0.14}
              dragMomentum={false}
              onPointerDownCapture={() => {
                draggedRef.current = false;
              }}
              // A tap on the disc with a little finger jitter starts a
              // Motion drag (3px threshold). Only a real swipe may swallow
              // the click that follows — under TAP_SLOP_PX it's still a tap.
              onDragEnd={(_, info) => {
                draggedRef.current = Math.abs(info.offset.x) > TAP_SLOP_PX;
                const swipe = info.offset.x + info.velocity.x * 0.18;
                const step = swipe < -W * 0.18 ? 1 : swipe > W * 0.18 ? -1 : 0;
                goTo(index + step, false);
              }}
              // A drag that ends on a case must not also open it.
              onClickCapture={(event) => {
                if (draggedRef.current) {
                  event.stopPropagation();
                  event.preventDefault();
                }
              }}
            >
              {slides.map((song, i) => (
                <div
                  key={song.id}
                  className="flex h-full shrink-0 items-center justify-center"
                  style={{ width: W }}
                  aria-hidden={i !== index}
                >
                  <CdCase
                    song={song}
                    size={S}
                    openScale={openScale}
                    // Every case is open while the section is: a swipe
                    // flips through open cases, and only the active one plays.
                    open={open}
                    flourish={flourish && i === index}
                    status={preview.key === song.id ? preview.status : "idle"}
                    playing={playing && preview.key === song.id}
                    progress={preview.progress}
                    ownerFirstName={ownerFirstName}
                    tracklist={tracklist}
                    interactive={i === index}
                    onOpen={openCase}
                    onClose={closeCase}
                    onTogglePlay={togglePlay}
                  />
                </div>
              ))}
            </motion.div>
          ) : null}
        </div>
      </div>

      {active ? (
        <NowPlayingBar
          song={active}
          playing={playing && preview.key === active.id}
          loading={preview.status === "loading" && preview.key === active.id}
          failed={preview.status === "error" && preview.key === active.id}
          onTogglePlay={togglePlay}
          onPauseForSheet={preview.pause}
        />
      ) : null}

      {slides.length > 1 || extraCount > 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          role="tablist"
          aria-label="Songs"
        >
          {slides.map((song, i) => {
            const thumb = musicArtworkSrc(song.artworkUrl, 72);
            return (
              <button
                key={song.id}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`${song.title} by ${song.artistName}`}
                onClick={() => goTo(i, false)}
                className={cn(
                  "relative size-9 overflow-hidden rounded-md bg-surface-alt shadow-sm ring-offset-2 ring-offset-background transition-[transform,opacity] duration-200 ease-out-strong active:scale-95 dark:bg-glass-bg-10",
                  i === index
                    ? "opacity-100 ring-2 ring-text-primary"
                    : "opacity-55",
                )}
              >
                {thumb ? (
                  // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Disc3 className="m-auto size-4 text-text-tertiary" />
                )}
              </button>
            );
          })}
          {extraCount > 0 ? (
            <button
              type="button"
              onClick={onOpenList}
              aria-label={`See all ${slides.length + extraCount} songs`}
              className="flex size-9 items-center justify-center rounded-md border border-border bg-surface-alt text-xs font-bold text-text-secondary shadow-sm transition-transform duration-150 ease-out-strong active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-10 dark:text-white"
            >
              +{extraCount}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function NowPlayingBar({
  song,
  playing,
  loading,
  failed,
  onTogglePlay,
  onPauseForSheet,
}: {
  song: ProfileMusicSong;
  playing: boolean;
  loading: boolean;
  failed: boolean;
  onTogglePlay: () => void;
  /** The sheet has its own player; the case's preview stops first. */
  onPauseForSheet: () => void;
}) {
  const hasPreview = Boolean(song.previewUrl) && !failed;
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-3 rounded-[24px] border border-border bg-surface-alt px-4 py-3 shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-05">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onTogglePlay}
            disabled={!hasPreview}
            aria-label={playing ? "Pause preview" : "Play preview"}
            className="flex size-11 shrink-0 items-center justify-center rounded-full bg-text-primary text-background shadow-md transition-transform duration-150 ease-out-strong active:scale-95 disabled:opacity-40"
          >
            {loading ? (
              <Loader2 className="size-5 animate-spin" />
            ) : playing ? (
              <Pause className="size-5" fill="currentColor" strokeWidth={0} />
            ) : (
              <Play
                className="ml-0.5 size-5"
                fill="currentColor"
                strokeWidth={0}
              />
            )}
          </button>
          <div className="flex min-w-0 flex-1 flex-col">
            <p className="flex min-w-0 items-center gap-1.5 text-sm font-semibold text-text-primary">
              <span className="truncate">{song.title}</span>
              {/* Inline, never a line of its own: every song's bar is the same
                  height, so swiping between songs can't move the page. */}
              {!hasPreview ? (
                <span className="shrink-0 rounded-full bg-text-tertiary/20 px-1.5 text-[9px] leading-[14px] font-semibold tracking-wide text-text-secondary uppercase">
                  No preview
                </span>
              ) : null}
              {song.explicit ? (
                <span className="shrink-0 rounded-[3px] bg-text-tertiary/25 px-1 text-[9px] leading-[14px] font-bold text-text-secondary">
                  <span aria-hidden>E</span>
                  <span className="sr-only">Explicit</span>
                </span>
              ) : null}
            </p>
            <p className="truncate text-xs text-text-secondary">
              {song.artistName}
              {song.albumName ? ` · ${song.albumName}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              onPauseForSheet();
              setSheetOpen(true);
            }}
            aria-label={`Share ${song.title}`}
            className="pointer-fine:hover:bg-surface-hover flex size-10 shrink-0 items-center justify-center rounded-full text-text-secondary transition-transform duration-150 ease-out-strong active:scale-90 dark:text-white"
          >
            <Send className="size-4.5" />
          </button>
        </div>
      </div>
      {/* Listen on — full width, so all three fit on a phone without wrapping. */}
      <MusicServiceChips
        song={song}
        className="scrollbar-hide flex-nowrap justify-center-safe overflow-x-auto"
      />
      <SongSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        music={musicAttachmentFromProfileSong(song)}
      />
    </div>
  );
}
